import { useEffect, useMemo, useRef, useState } from 'react';

import type { MapFloorLevel, MapObjectInstance } from '../../content/schema/map';
import { getObjectDefinition } from '../../content/registry/objectRegistry';
import { useMapEditorStore } from '../store/mapEditorStore';
import { useEngineRuntimeStore } from '../../store/engineRuntimeStore';
import { ObjectThumbnail } from '../thumbnails/ObjectThumbnail';

/**
 * Optional game-supplied grouping: maps a placed instance to the room/zone it
 * sits in. When omitted, the sidebar lists everything under a single group, so
 * the editor works for any game without game-specific floor geometry.
 */
export type SceneObjectRoomResolver = (
  instance: MapObjectInstance,
) => { id: string; name: string } | null;

interface ObjectListSidebarProps {
  floor: MapFloorLevel;
  roomResolver?: SceneObjectRoomResolver;
}

interface GroupedInstances {
  roomId: string;
  roomName: string;
  instances: MapObjectInstance[];
}

function groupInstancesByRoom(
  instances: MapObjectInstance[],
  roomResolver?: SceneObjectRoomResolver,
): GroupedInstances[] {
  if (!roomResolver) {
    if (instances.length === 0) return [];
    return [{ roomId: '__all', roomName: 'All Objects', instances }];
  }

  const buckets = new Map<string, GroupedInstances>();
  for (const instance of instances) {
    const room = roomResolver(instance);
    const roomId = room?.id ?? '__unassigned';
    const roomName = room?.name ?? 'Unassigned';
    if (!buckets.has(roomId)) {
      buckets.set(roomId, { roomId, roomName, instances: [] });
    }
    buckets.get(roomId)!.instances.push(instance);
  }

  return [...buckets.values()].sort((a, b) => a.roomName.localeCompare(b.roomName));
}

function formatXZ(position: [number, number, number]) {
  return `[${position[0].toFixed(1)}, ${position[2].toFixed(1)}]`;
}

interface ContextMenu {
  x: number;
  y: number;
  instanceId: string;
}

export function ObjectListSidebar({ floor, roomResolver }: ObjectListSidebarProps) {
  const objectInstances = useMapEditorStore((state) => state.objectInstances);
  const selectedObjectIds = useMapEditorStore((state) => state.selectedObjectIds);
  const setSelectedObjectIds = useMapEditorStore((state) => state.setSelectedObjectIds);
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const duplicateObject = useMapEditorStore((state) => state.duplicateObject);
  const deleteObject = useMapEditorStore((state) => state.deleteObject);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const focusOnInstance = useMapEditorStore((state) => state.focusOnInstance);
  const [query, setQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const lastClickIdRef = useRef<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredInstances = useMemo(() => {
    const scoped = objectInstances.filter((instance) => instance.floor === floor);
    if (!query.trim()) return scoped;
    const needle = query.trim().toLowerCase();
    return scoped.filter((instance) => {
      const def = getObjectDefinition(instance.assetId);
      const haystack = [
        instance.name,
        def?.name,
        instance.assetId,
        instance.id,
        ...(instance.tags ?? []),
      ].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [objectInstances, floor, query]);

  const groups = useMemo(
    () => groupInstancesByRoom(filteredInstances, roomResolver),
    [filteredInstances, roomResolver],
  );

  const flatListItems = useMemo(() => {
    const list: Array<
      | { type: 'header'; id: string; roomName: string; count: number }
      | { type: 'row'; id: string; instance: MapObjectInstance }
    > = [];
    for (const group of groups) {
      list.push({
        type: 'header',
        id: `header-${group.roomId}`,
        roomName: group.roomName,
        count: group.instances.length,
      });
      for (const inst of group.instances) {
        list.push({
          type: 'row',
          id: inst.id,
          instance: inst,
        });
      }
    }
    return list;
  }, [groups]);

  const itemOffsets = useMemo(() => {
    const offsets: number[] = [];
    let current = 0;
    for (const item of flatListItems) {
      offsets.push(current);
      current += item.type === 'header' ? 36 : 56;
    }
    return { offsets, totalHeight: current };
  }, [flatListItems]);

  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [contextMenu]);

  const handleRowClick = (instanceId: string, event: React.MouseEvent) => {
    if (event.shiftKey && lastClickIdRef.current) {
      // Range select within visible (flattened) list
      const flat = groups.flatMap((g) => g.instances.map((i) => i.id));
      const startIdx = flat.indexOf(lastClickIdRef.current);
      const endIdx = flat.indexOf(instanceId);
      if (startIdx >= 0 && endIdx >= 0) {
        const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        setSelectedObjectIds(flat.slice(lo, hi + 1));
        return;
      }
    }
    if (event.ctrlKey || event.metaKey) {
      toggleSelectObject(instanceId);
    } else {
      selectObject(instanceId);
    }
    lastClickIdRef.current = instanceId;
  };

  const handleRowContextMenu = (instanceId: string, event: React.MouseEvent) => {
    event.preventDefault();
    if (!selectedObjectIds.includes(instanceId)) {
      selectObject(instanceId);
    }
    setContextMenu({ x: event.clientX, y: event.clientY, instanceId });
  };

  const totalCount = objectInstances.filter((inst) => inst.floor === floor).length;

  return (
    <section className="editor-list-panel">
      <header className="editor-section-header">
        <div>
          <span className="editor-section-eyebrow">Scene Objects</span>
          <h3>{filteredInstances.length} of {totalCount}</h3>
        </div>
      </header>

      <label className="editor-search">
        <span>Search</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="name, id, tag..."
        />
      </label>

      <div
        ref={containerRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className="editor-list-groups"
        style={{
          overflowY: 'auto',
          height: '100%',
          maxHeight: '450px',
          position: 'relative'
        }}
      >
        {groups.length === 0 ? (
          <div className="editor-empty-state">
            <p>No objects on this floor match the current filter.</p>
          </div>
        ) : (
          <div style={{ height: `${itemOffsets.totalHeight}px`, position: 'relative', width: '100%' }}>
            {(() => {
              let startIndex = 0;
              for (let i = 0; i < flatListItems.length; i++) {
                const height = flatListItems[i]!.type === 'header' ? 36 : 56;
                if (itemOffsets.offsets[i]! + height >= scrollTop) {
                  startIndex = Math.max(0, i - 5);
                  break;
                }
              }
              let endIndex = flatListItems.length - 1;
              const containerHeight = 450;
              for (let i = startIndex; i < flatListItems.length; i++) {
                if (itemOffsets.offsets[i]! > scrollTop + containerHeight) {
                  endIndex = Math.min(flatListItems.length - 1, i + 5);
                  break;
                }
              }

              const visibleItems = flatListItems.slice(startIndex, endIndex + 1);

              return visibleItems.map((item, idx) => {
                const absoluteIndex = startIndex + idx;
                const top = itemOffsets.offsets[absoluteIndex];

                if (item.type === 'header') {
                  return (
                    <div
                      key={item.id}
                      className="editor-list-group-header"
                      style={{
                        position: 'absolute',
                        top: `${top}px`,
                        height: '36px',
                        left: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 8px',
                        background: 'rgba(255, 255, 255, 0.04)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
                      }}
                    >
                      <span className="editor-list-group-name" style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', color: '#aaa' }}>{item.roomName}</span>
                      <span className="editor-list-group-count" style={{ fontSize: '10px', background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '10px' }}>{item.count}</span>
                    </div>
                  );
                }

                const instance = item.instance;
                const def = getObjectDefinition(instance.assetId);
                const name = instance.name ?? def?.name ?? instance.assetId;
                const isSelected = selectedObjectIds.includes(instance.id);

                return (
                  <div
                    key={instance.id}
                    className={`editor-list-row${isSelected ? ' is-selected' : ''}`}
                    onClick={(event) => handleRowClick(instance.id, event)}
                    onContextMenu={(event) => handleRowContextMenu(instance.id, event)}
                    onMouseEnter={() => setHoveredObjectId(instance.id)}
                    onMouseLeave={() => {
                      if (useMapEditorStore.getState().hoveredObjectId === instance.id) {
                        setHoveredObjectId(null);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: `${top}px`,
                      height: '56px',
                      left: 0,
                      right: 0,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.02)',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="editor-list-thumb" style={{ marginRight: '8px' }}>
                      <ObjectThumbnail assetId={instance.assetId} label={name} size={40} />
                    </div>
                    <div className="editor-list-meta" style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="editor-list-meta-name" style={{ fontSize: '13px', fontWeight: '500' }}>{name}</span>
                      <span className="editor-list-meta-sub" style={{ fontSize: '11px', color: '#777' }}>
                        {formatXZ(instance.transform.position)} · {instance.id.split('-').slice(-1)[0]}
                      </span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {contextMenu ? (
        <div
          className="editor-context-menu ui-panel"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              const currentFloor = useEngineRuntimeStore.getState().currentFloor as MapFloorLevel;
              focusOnInstance(contextMenu.instanceId, { currentFloor });
              setContextMenu(null);
            }}
          >
            Focus
          </button>
          <button
            type="button"
            onClick={() => {
              duplicateObject(contextMenu.instanceId);
              setContextMenu(null);
            }}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="is-danger"
            onClick={() => {
              deleteObject(contextMenu.instanceId);
              setContextMenu(null);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}
    </section>
  );
}
